import { PersonLike } from "@lib/infrastructure";
import { getAcceptedGroups, getGroupOfficeDayCountsByDate, GroupWithWorkdayCount, useAppSelector } from "@lib/store";
import { AddGroupMembersSectionItem } from "./AddGroupMembersSectionItem";
import { useTranslation } from "react-i18next";
import { animated, config, useTransition } from "@react-spring/web";
import { LocalDateString } from "@lib/store/src/data/timeslots/apiTypes";
import { useMemo } from "react";

export const AddGroupMembersSection = ({
  onSelect,
  show,
  dateKey,
}: {
  onSelect: (invitees: PersonLike[], groupId: string) => void;
  show: boolean;
  dateKey: LocalDateString;
}) => {
  const { t } = useTranslation();

  const acceptedGroups = useAppSelector(state => getAcceptedGroups(state));
  const groupCountsForDate = useAppSelector(state => getGroupOfficeDayCountsByDate(state, dateKey));

  // Filter out groups where the current user is the only accepted member
  const groups = acceptedGroups.filter(
    // if im the only accepted member, filter out
    group => {
      const acceptedMemberCount = group.members.filter(member => member.status === "Accepted").length;
      return acceptedMemberCount > 1;
    },
  );

  const manualGroupsWithWorkdayCount = useMemo(() => {
    return groups.reduce<GroupWithWorkdayCount[]>((acc, group) => {
      const countObj = groupCountsForDate?.find(c => c.groupId === group.id);
      if (countObj) {
        acc.push({ ...group, memberCount: countObj.memberCount, workdayCount: countObj.count || 0 });
      } else {
        acc.push({ ...group, memberCount: group.members.filter(m => m.status === "Accepted").length, workdayCount: 0 });
      }
      return acc;
    }, []);
  }, [groups, groupCountsForDate]);

  const transitions = useTransition(show, {
    delay: 100,
    initial: { opacity: 1, transform: "translate3d(0, 0px, 0) scale(1)" },
    from: { opacity: 0, transform: "translate3d(0, 10px, 0) scale(1)" },
    enter: { opacity: 1, transform: "translate3d(0, 0px, 0) scale(1)" },
    leave: { opacity: 0, transform: "translate3d(0, 10px, 0) scale(1)" },
    config: config.stiff,
  });

  if (manualGroupsWithWorkdayCount.length === 0) {
    return null;
  }

  return transitions(
    (style, item) =>
      item && (
        <animated.div
          style={style}
          className="grid gap-4">
          <h4>{t("groups:Booking.SectionTitle")}</h4>
          <div className="divide-y">
            {manualGroupsWithWorkdayCount.map(group => (
              <AddGroupMembersSectionItem
                key={group.id}
                onSelect={onSelect}
                group={group}
              />
            ))}
          </div>
        </animated.div>
      ),
  );
};
